// Config-driven numeric display for AntD <InputNumber>. Local standard —
// resolve a value class's decimal policy from cfg.number_formats (delivered
// via globalConfigs) and spread the returned props into the input:
//
//   const currencyFmt = useNumberFormat('currency')
//   <InputNumber {...currencyFmt} ... />
//
// Policy semantics (cfg.number_formats):
//   min_decimals — the display pads with zeros up to this many decimals
//                  (currency always shows 2, fx rates at least 8);
//                  0 = decimals appear only when the value has them.
//   max_decimals — the display caps decimals at this many.
// Thousands separators always apply to the integer part; the parser strips
// them so the numeric value is untouched. While the user is typing, only the
// separators are applied (no padding/trimming) so the formatter never fights
// the keystroke; the full policy applies on blur / programmatic display.

import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { NumberFormatModel } from '../../state_manager/data_models/number_format_model.ts'

export interface NumberFormatPolicy {
  /** display: pad to at least this many decimals */
  min_decimals: number
  /** display: TRUNCATE the rendered value to at most this many decimals. A
   *  display may legitimately be coarser than the number behind it (a valuation
   *  shown without decimals), so this is NOT a constraint on the value. */
  max_decimals: number
  /** input: decimals an EDITED value may be SAVED with. null/absent = no
   *  constraint. Separate from max_decimals precisely so that changing how a
   *  figure is displayed can never change what gets stored. */
  input_decimals?: number | null
}

const addThousandsSeparators = (intPart: string): string =>
  intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

export const formatWithPolicy = (
  value: string | number | undefined,
  policy?: NumberFormatPolicy | null,
  userTyping = false
): string => {
  if (value === null || value === undefined || value === '') return ''
  const raw = `${value}`
  const negative = raw.startsWith('-')
  const [intRaw, decRaw] = (negative ? raw.slice(1) : raw).split('.')
  const int = addThousandsSeparators(intRaw)
  let dec = decRaw

  if (!userTyping && policy) {
    // ROUND at max_decimals, do not take a prefix of the digits. Slicing made
    // the display a PREFIX of the number, so any figure carrying float noise
    // read low: 246128.8 + 126439.4 is 372568.19999999995 in IEEE doubles and
    // showed as 372,568.19 - a cent under the true total. toFixed rounds and
    // carries (2.999 -> 3.00), so the integer part is recomputed with it.
    const numeric = typeof value === 'number' ? value : Number(raw)
    if (Number.isFinite(numeric)) {
      const fixed = Math.abs(numeric).toFixed(policy.max_decimals)
      const [fixedInt, fixedDec] = fixed.split('.')
      let rounded = fixedDec ?? ''
      // toFixed pads to max_decimals; the policy only guarantees min_decimals,
      // so drop the padding the old prefix form never produced
      while (rounded.length > policy.min_decimals && rounded.endsWith('0')) {
        rounded = rounded.slice(0, -1)
      }
      while (rounded.length < policy.min_decimals) rounded += '0'
      const roundedSign = numeric < 0 ? '-' : ''
      const roundedInt = addThousandsSeparators(fixedInt)
      return rounded ? `${roundedSign}${roundedInt}.${rounded}` : `${roundedSign}${roundedInt}`
    }
    // not a finite number (a mid-edit string, say): fall back to the prefix form
    dec = (dec ?? '').slice(0, policy.max_decimals)
    while (dec.length < policy.min_decimals) dec += '0'
  }

  const sign = negative ? '-' : ''
  // preserve a trailing '.' mid-keystroke ("1234.") so typing decimals works
  if (userTyping && decRaw !== undefined) return `${sign}${int}.${decRaw}`
  return dec ? `${sign}${int}.${dec}` : `${sign}${int}`
}

export const thousandsParser = (value: string | undefined): string =>
  (value ?? '').replace(/,/g, '')

/**
 * antd InputNumber props for a cfg.number_formats policy.
 *
 * `precision` is what keeps the saved value honest. formatWithPolicy truncates
 * the DISPLAY to max_decimals and leaves the value untouched, so without it an
 * input can hold - and submit - a digit the operator cannot see: a capital call
 * payment typed as 50000 was booked as 50000.005. The guard is driven by
 * input_decimals, never by max_decimals, because the latter is a display
 * setting that is allowed to be coarser than the number.
 */
export const numberFormatProps = (policy?: NumberFormatPolicy | null) => ({
  formatter: (value: string | number | undefined, info?: { userTyping?: boolean }) =>
    formatWithPolicy(value, policy, Boolean(info?.userTyping)),
  parser: thousandsParser,
  ...(policy?.input_decimals != null ? { precision: policy.input_decimals } : {})
})

// Fallbacks when cfg.number_formats has no row for a key (fresh DB before the
// seed, or a new key) — separators still apply, decimals as typed.
const DEFAULT_POLICY: NumberFormatPolicy = { min_decimals: 0, max_decimals: 10 }

/** InputNumber formatter/parser props for a cfg.number_formats key. */
export function useNumberFormat(key: string) {
  const numberFormats = useSelector(
    (state: any) => state.globalConfigs?.number_formats ?? []
  ) as NumberFormatModel[]

  return useMemo(() => {
    const row = numberFormats.find((f) => f.key === key)
    return numberFormatProps(row ?? DEFAULT_POLICY)
  }, [numberFormats, key])
}

/** Plain thousands separators, decimals as typed (no cfg policy). */
export const thousandsProps = numberFormatProps(null)

/**
 * Decimal-fraction <-> percent conversion for percent-edited columns backed
 * by decimal storage (0.02 edited as 2%). Local standard: a bare
 * `value * 100` controlled round-trip re-renders float noise mid-keystroke
 * (0.029 * 100 === 2.9000000000000004), rewriting the input text and
 * throwing the cursor to the end of the noise digits. decimalToPercent
 * rounds the noise away (10dp of percent — far beyond any editing
 * precision); percentToDecimal is the matching store conversion.
 */
export const decimalToPercent = (value: number | null | undefined): number | null =>
  value == null ? null : Math.round(value * 1e12) / 1e10

export const percentToDecimal = (value: number | string | null | undefined): number | null =>
  value == null || value === '' ? null : Number(value) / 100

/**
 * The raw cfg.number_formats policy for a key — for callers that need to reason
 * about the policy itself rather than just apply it (NumberText, which has to
 * know whether a figure is showing everything it holds).
 */
export function useNumberPolicy(key: string): NumberFormatPolicy {
  const numberFormats = useSelector(
    (state: any) => state.globalConfigs?.number_formats ?? []
  ) as NumberFormatModel[]

  return useMemo(
    () => numberFormats.find((f) => f.key === key) ?? DEFAULT_POLICY,
    [numberFormats, key]
  )
}

/**
 * The figure in full: thousands separators and every decimal the value holds,
 * padded out to the policy's min_decimals so money still reads as money
 * (268,606.80, not 268,606.8). A null policy never truncates, so nothing is
 * lost — this only ever adds trailing zeros.
 */
export const formatFull = (
  value: number | null | undefined,
  policy?: NumberFormatPolicy | null
): string => {
  if (value == null) return ''
  const full = formatWithPolicy(value, null)
  const min = policy?.min_decimals ?? 0
  if (min === 0) return full
  const [int, dec = ''] = full.split('.')
  return dec.length >= min ? full : `${int}.${dec.padEnd(min, '0')}`
}

/**
 * Is the policy's rendering of this value the whole story, or an abbreviation?
 * Compares by parsing the rendered figure back, so it catches a rounded figure
 * as well as dropped decimals.
 */
export const isAbbreviated = (
  value: number | null | undefined,
  policy?: NumberFormatPolicy | null
): boolean => {
  if (value == null || !Number.isFinite(value)) return false
  const shown = Number(formatWithPolicy(value, policy).replace(/,/g, ''))
  return Number.isFinite(shown) && shown !== value
}

/**
 * Plain-TEXT display for a cfg.number_formats key — the read-only sibling of
 * useNumberFormat, for table cells / totals rows that render amounts as text
 * rather than through an <InputNumber>. Accounting amounts ('currency') pad
 * to exactly two decimals; never the bare toLocaleString single-decimal look.
 */
export function useNumberDisplay(key: string): (value: number | null | undefined) => string {
  const numberFormats = useSelector(
    (state: any) => state.globalConfigs?.number_formats ?? []
  ) as NumberFormatModel[]

  return useMemo(() => {
    const row = numberFormats.find((f) => f.key === key) ?? DEFAULT_POLICY
    return (value: number | null | undefined) =>
      value == null ? '-' : formatWithPolicy(value, row)
  }, [numberFormats, key])
}
